import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import HeroSlideshow from '../components/HeroSlideshow';
import RegistryVerify from '../components/RegistryVerify';
import ResourceHub from '../components/ResourceHub';
import Seo, { SITE_URL, SITE_NAME } from '../components/Seo';
import './Home.css';

const API = process.env.REACT_APP_API_URL || 'https://api.stratosstandard.com';

/* Registry-safe FAQ — answers double as the YMYL deflection: they state, in
   Google-readable structured data, that we recognize achievements rather than
   screen or diagnose development. */
const FAQ = [
  {
    q: 'Is Stratos Standard a developmental assessment or screening?',
    a: 'No. Stratos Standard is an achievement recognition registry. Our play-based ' +
       'verification sessions confirm skills a child can already demonstrate and record ' +
       'them as a permanent registry entry. We do not screen, diagnose, or give ' +
       'developmental or medical advice — for that, talk with your pediatrician.',
  },
  {
    q: 'What is the Stratos Registry?',
    a: 'A permanent, searchable record of verified early achievements. Every certificate ' +
       'carries an ID that anyone can look up to confirm it is genuine.',
  },
  {
    q: 'What happens in a verification session?',
    a: 'A trained SKIES consultant guides your child through a warm, play-based session ' +
       'and verifies the specific achievement you chose. It feels like play, not a test.',
  },
  {
    q: 'What ages do you work with?',
    a: 'Children from 0 to 6 years old, across every stage of early childhood.',
  },
];

/* ── Fade-in-on-scroll hook ── */
function useFadeIn() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) { e.target.classList.add('visible'); obs.unobserve(e.target); }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    el.querySelectorAll('.fade-in-up').forEach(n => obs.observe(n));
    return () => obs.disconnect();
  }, []);
  return ref;
}

const SERVICES = [
  {
    icon: '🔵',
    title: 'Object Identification',
    sub: 'Foundational · Advanced · Mastery',
    desc: "See how a child recognizes, names, and reasons about objects, from simple naming to sorting by several features at once.",
    color: 'card-green',
  },
  {
    icon: '🔢',
    title: 'Mathematics',
    sub: 'Foundational · Advanced',
    desc: 'Look at early number skills like counting, matching numbers to objects, and spotting patterns.',
    color: 'card-gold',
  },
  {
    icon: '📖',
    title: 'Speech & Language',
    sub: 'Storytelling · Sounds & Early Reading',
    desc: 'See how a child retells stories, draws conclusions, plays with sounds, and builds sentences.',
    color: 'card-blue',
  },
];

const STEPS = [
  { num: '01', title: 'Sign Up', desc: 'Create your account and tell us about your child.' },
  { num: '02', title: 'Choose a Service', desc: 'Select the certificate and level you\'d like your child recognized for.' },
  { num: '03', title: 'Book a Time Slot', desc: 'Select a convenient appointment with a SKIES consultant.' },
  { num: '04', title: 'Session Day', desc: 'Your SKIES consultant guides your child through a warm, play-based session.' },
  { num: '05', title: 'Receive Certification', desc: 'Get your official Stratos Standard certificate, ready to frame!' },
];

// Display label for the secondary author line from the structured review fields.
function childLabel(r) {
  if (!r.childFirstName) return '';
  return `Parent of ${r.childFirstName}${r.childAge ? `, age ${r.childAge}` : ''}`;
}

const BLOG_POSTS = [
  {
    slug: 'what-is-early-achievement',
    date: 'March 10, 2026',
    tag: 'Development',
    title: 'What Does "Early Achievement" Really Mean for Your Child?',
    excerpt: 'What it means to recognize and celebrate the things your child can already do — and why a permanent record of it matters.',
  },
  {
    slug: 'aero-philosophy',
    date: 'February 28, 2026',
    tag: 'Aero',
    title: 'AERO: Achieving Early Results & Outcomes',
    excerpt: "Our philosophy isn't about pressure. It's about seeing your child clearly and celebrating who they already are.",
  },
  {
    slug: 'preparing-for-assessment',
    date: 'February 14, 2026',
    tag: 'Tips',
    title: '5 Ways to Help Your Child Feel Ready for Their Session',
    excerpt: 'A calm, confident child makes for the best session. Here are our consultant-approved tips to prepare.',
  },
];

export default function Home() {
  const s0 = useFadeIn();
  const s1 = useFadeIn();
  const s2 = useFadeIn();
  const s3 = useFadeIn();
  const s4 = useFadeIn();
  const s5 = useFadeIn();

  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/reviews`);
        const data = await res.json();
        if (!cancelled) setTestimonials(Array.isArray(data.reviews) ? data.reviews.slice(0, 3) : []);
      } catch (_) {
        if (!cancelled) setTestimonials([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const jsonLd = useMemo(() => ([
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      // The registry as a first-class searchable dataset — anchors the
      // "Achievement Registry" identity in structured data.
      '@context': 'https://schema.org',
      '@type': 'Dataset',
      name: 'Stratos Standard Achievement Registry',
      description:
        'A permanent, searchable registry of verified early-childhood achievements ' +
        'for children ages 0–6. Each entry records a play-based verification result.',
      url: `${SITE_URL}/`,
      creator: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
      keywords: ['early achievement', 'children registry', 'verification', 'recognition'],
    },
  ]), []);

  return (
    <>
      <Seo
        title="Early Achievement Registry for Children 0–6"
        description="Celebrate and permanently record your child's early achievements. Warm, play-based verification sessions and a searchable registry entry for children ages 0–6 — recognition, not assessment."
        path="/"
        jsonLd={jsonLd}
      />

      {/* ════ ZONE 1 — High-intent header (above the fold) ════ */}
      <HeroSlideshow />

      {/* ════ ZONE 2 — Interactive value proposition (mid-page transition) ════ */}
      {/* SKIES Intro banner */}
      <section className="skies-banner" ref={s0}>
        <div className="container skies-banner-inner">
          <div className="skies-acronym fade-in-up">
            {['S','K','I','E','S'].map((letter, i) => (
              <div key={i} className="skies-letter-block">
                <span className="skies-letter">{letter}</span>
                <span className="skies-word">{['Supporting','Kids','In','Early','Success'][i]}</span>
              </div>
            ))}
          </div>
          <div className="skies-tagline fade-in-up">
            <p className="eyebrow">Our Framework</p>
            <h2>The SKIES Standard</h2>
            <p>Every session follows our SKIES framework, a research-backed approach to recognizing, affirming, and celebrating early childhood achievement with warmth and care.</p>
            <Link to="/services" className="btn btn-aero" style={{ marginTop: '20px' }}>
              Explore Our Approach
            </Link>
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section className="section-pad" ref={s1}>
        <div className="container text-center">
          <p className="eyebrow fade-in-up">What We Offer</p>
          <h2 className="section-title fade-in-up">Certificate Programs</h2>
          <p className="section-subtitle fade-in-up">
            Three certificate families, each with progressive levels, designed to recognize the specific skills your child has mastered.
          </p>
          <div className="services-grid">
            {SERVICES.map((s, i) => (
              <div key={i} className={`service-card fade-in-up ${s.color}`}>
                <div className="svc-icon">{s.icon}</div>
                <span className="svc-sub">{s.sub}</span>
                <h3 className="svc-title">{s.title}</h3>
                <p className="svc-desc">{s.desc}</p>
                <Link to="/book" className="btn btn-primary btn-sm svc-btn">
                  Book Now
                </Link>
              </div>
            ))}
          </div>
          <Link to="/services" className="btn btn-dark" style={{ marginTop: '40px' }}>
            See Full Details &amp; Pricing
          </Link>
        </div>
      </section>

      {/* ── Aero section ── */}
      <section className="aero-section" ref={s2}>
        <div className="container aero-inner">
          <div className="aero-visual fade-in-up" aria-hidden="true">
            <div className="aero-circle aero-c1" />
            <div className="aero-circle aero-c2" />
            <div className="aero-circle aero-c3" />
            <div className="aero-text-overlay">
              <span>AERO</span>
            </div>
          </div>
          <div className="aero-content fade-in-up">
            <p className="eyebrow" style={{ color: '#3ba3e8' }}>Our Philosophy</p>
            <h2 style={{ color: '#ffffff', fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem,4vw,2.8rem)', marginBottom: '16px' }}>
              Achieving Early<br />Results &amp; Outcomes
            </h2>
            <div className="accent-line accent-line-left" />
            <p style={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.8, marginTop: '20px', marginBottom: '28px' }}>
              AERO is the heart of everything we do at Stratos Standard. We believe children who show early mastery deserve more than a high-five. They deserve <em>recognition</em>. Our SKIES consultants are trained to see potential clearly, observe it fairly, and honor it beautifully.
            </p>
            <ul className="aero-list">
              {['Research-backed certification framework','Warm, play-based approach','Trained SKIES consultant network','Official certification issued same day','Milestone tracking for parents'].map((item, i) => (
                <li key={i}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d4a017" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  {item}
                </li>
              ))}
            </ul>
            <Link to="/book" className="btn btn-primary btn-lg" style={{ marginTop: '32px' }}>
              Book a Session
            </Link>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="section-pad how-section" ref={s3}>
        <div className="container text-center">
          <p className="eyebrow fade-in-up">Simple Process</p>
          <h2 className="section-title fade-in-up">How It Works</h2>
          <div className="accent-line fade-in-up" />
          <div className="steps-row">
            {STEPS.map((step, i) => (
              <div key={i} className="step-item fade-in-up">
                <div className="step-num">{step.num}</div>
                {i < STEPS.length - 1 && <div className="step-connector" aria-hidden="true" />}
                <h4 className="step-title">{step.title}</h4>
                <p className="step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
          <Link to="/book" className="btn btn-primary btn-lg fade-in-up" style={{ marginTop: '56px' }}>
            Start Your Journey
          </Link>
        </div>
      </section>

      {/* ════ ZONE 3 — Database & authority verification (value justification) ════ */}
      <RegistryVerify />

      {/* ── Testimonials ── */}
      {testimonials.length > 0 && (
        <section className="section-pad testimonials-section" ref={s4}>
          <div className="container text-center">
            <p className="eyebrow fade-in-up">Families Love Us</p>
            <h2 className="section-title fade-in-up">What Parents Are Saying</h2>
            <div className="accent-line fade-in-up" />
            <div className="grid-3" style={{ marginTop: '48px' }}>
              {testimonials.map((t) => (
                <div key={t.reviewId} className="testimonial-card fade-in-up">
                  <div className="stars" style={{ marginBottom: '16px' }}>
                    {'★'.repeat(Math.max(1, Math.min(5, t.rating || 5)))}
                  </div>
                  <blockquote className="testi-quote">"{t.quote}"</blockquote>
                  <div className="testi-author">
                    <div className="testi-avatar">{(t.parentName || '?')[0]}</div>
                    <div>
                      <p className="testi-name">{t.parentName}</p>
                      <p className="testi-child">{childLabel(t)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/reviews" className="btn btn-outline-dark fade-in-up" style={{ marginTop: '40px' }}>
              Read &amp; Share Reviews
            </Link>
          </div>
        </section>
      )}

      {/* ── Blog preview ── */}
      <section className="section-pad blog-preview" ref={s5}>
        <div className="container">
          <div className="section-header-row">
            <div>
              <p className="eyebrow fade-in-up">Latest from Our Blog</p>
              <h2 className="section-title fade-in-up" style={{ marginBottom: 0 }}>Insights &amp; Updates</h2>
            </div>
            <Link to="/blog" className="btn btn-outline-dark fade-in-up">View All Posts</Link>
          </div>
          <div className="grid-3" style={{ marginTop: '40px' }}>
            {BLOG_POSTS.map((post, i) => (
              <Link to={`/blog/${post.slug}`} key={i} className="blog-card fade-in-up">
                <div className="blog-img-placeholder">
                  <div className="blog-img-bg" style={{ '--idx': i }} />
                </div>
                <div className="blog-card-body">
                  <div className="blog-meta">
                    <span className="badge badge-sky">{post.tag}</span>
                    <span className="blog-date">{post.date}</span>
                  </div>
                  <h3 className="blog-title">{post.title}</h3>
                  <p className="blog-excerpt">{post.excerpt}</p>
                  <span className="blog-read-more">Read more →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA banner ── */}
      <section className="cta-banner">
        <div className="container cta-inner">
          <div>
            <p className="eyebrow" style={{ color: '#3ba3e8' }}>Ready to Celebrate Your Child?</p>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.8rem,3.5vw,2.6rem)', color: '#ffffff', marginBottom: '12px' }}>
              Book Your SKIES Session Today
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.7)', maxWidth: '520px' }}>
              Join hundreds of families who've given their children the recognition they deserve. Sessions available for children ages 0 to 6.
            </p>
          </div>
          <div className="cta-actions">
            <Link to="/book" className="btn btn-primary btn-lg">Book Now</Link>
            <Link to="/contact" className="btn btn-outline btn-lg">Ask a Question</Link>
          </div>
        </div>
      </section>

      {/* ── FAQ (visible content backing the FAQPage structured data) ── */}
      <section className="section-pad home-faq">
        <div className="container">
          <div className="text-center">
            <p className="eyebrow">Good to Know</p>
            <h2 className="section-title">Common Questions</h2>
          </div>
          <div className="faq-list">
            {FAQ.map((f) => (
              <details key={f.q} className="faq-item">
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ════ ZONE 4 — Curated resource hub + E-E-A-T anchor (above the footer) ════ */}
      <ResourceHub />
    </>
  );
}
